/**
 * Module dependencies.
 */

import { Logo, getAuthUser } from './brand';
import { appRoutes } from 'app/routes';
import { useNavigate } from 'react-router-dom';
import React, { useMemo } from 'react';

/**
 * Footer link column.
 */

function FooterColumn({
  items,
  title
}: {
  items: { label: string; onClick?: () => void }[];
  title: string;
}): JSX.Element {
  return (
    <div>
      <div className={'text-xs font-semibold uppercase tracking-[0.08em] text-neutral-400'}>{title}</div>

      <ul className={'m-0 mt-4 list-none space-y-3 p-0'}>
        {items.map(item => (
          <li key={item.label}>
            <button
              className={'p-0 text-left text-sm text-neutral-600 transition-colors hover:text-[#1B752B]'}
              onClick={item.onClick}
              type={'button'}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Export `SiteFooter` component — shared across the landing and legal pages.
 */

export function SiteFooter(): JSX.Element {
  const navigate = useNavigate();
  const authUser = useMemo(() => getAuthUser(), []);

  const goFeatures = () => {
    const el = document.getElementById('features');

    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });

      return;
    }

    navigate({ hash: 'features', pathname: appRoutes.home });
  };

  return (
    <footer className={'border-t'} style={{ borderColor: 'rgba(228,228,231,0.8)' }}>
      <div className={'mx-auto w-full max-w-6xl px-6 py-12'}>
        <div className={'grid grid-cols-1 gap-10 sm:grid-cols-[1.6fr_1fr_1fr_1fr] sm:gap-12'}>
          <div>
            <button aria-label={'Contab home'} onClick={() => navigate(appRoutes.home)} type={'button'}>
              <Logo size={32} />
            </button>

            <p className={'mt-4 max-w-xs pl-[30px] text-sm text-neutral-500'}>
              {'Personal finance for people who want the whole picture — accounts, projects, crypto and notes.'}
            </p>
          </div>

          <FooterColumn
            items={[
              { label: 'Features', onClick: goFeatures },
              authUser
                ? { label: 'Go to dashboard', onClick: () => navigate(appRoutes.dashboard.home.base) }
                : { label: 'Sign in', onClick: () => navigate(appRoutes.login) }
            ]}
            title={'Product'}
          />

          <FooterColumn
            items={[
              { label: 'FAQs', onClick: () => navigate(appRoutes.faqs) },
              { label: 'Status', onClick: () => navigate(appRoutes.status) }
            ]}
            title={'Support'}
          />

          <FooterColumn
            items={[
              { label: 'Terms & conditions', onClick: () => navigate(appRoutes.legal.terms) },
              { label: 'Privacy policy', onClick: () => navigate(appRoutes.legal.privacy) },
              { label: 'Cookie policy', onClick: () => navigate(appRoutes.legal.cookies) },
              { label: 'Security', onClick: () => navigate(appRoutes.legal.security) }
            ]}
            title={'Legal'}
          />
        </div>

        <div
          className={
            'mt-12 flex flex-col items-center justify-center gap-1 border-t pt-[22px] text-xs text-neutral-400 sm:flex-row sm:gap-2'
          }
          style={{ borderColor: 'rgba(228,228,231,0.9)' }}
        >
          <span>{`© ${new Date().getFullYear()} Contab. All rights reserved.`}</span>

          <span aria-hidden className={'hidden sm:inline'}>
            {'·'}
          </span>

          <span>
            {'Powered by '}

            <a
              className={
                'font-medium text-neutral-500 underline-offset-2 transition-colors hover:text-neutral-700 hover:underline'
              }
              href={'https://franciscomorais.com'}
              rel={'noopener noreferrer'}
              target={'_blank'}
            >
              {'Francisco Morais'}
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
